import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Quote, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATIC_TESTIMONIALS = [
    {
        id: "static-1",
        quote: "Cindy a été une présence rassurante et lumineuse tout au long de ma grossesse. Son écoute m'a permis d'aborder l'accouchement avec confiance.",
        name: "Léa",
        role: "Maman de Jules",
    },
    {
        id: "static-2",
        quote: "Un accompagnement précieux pour mon conjoint et moi. Elle a su trouver les mots justes et nous offrir des outils concrets pour la préparation à la naissance.",
        name: "Camille & Thomas",
        role: "Parents d'Eliott",
    },
    {
        id: "static-3",
        quote: "Bien plus qu'une accompagnante, Cindy est devenue un véritable repère. Sa douceur et son professionnalisme nous ont accompagnés jusqu'au post-partum.",
        name: "Marion",
        role: "Maman d'Iris",
    },
];

export default function Testimonials() {
    const [items, setItems] = useState(STATIC_TESTIMONIALS);
    const [name, setName] = useState("");
    const [message, setMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API}/testimonials`);
                if (!res.ok) return;
                const approved = await res.json();
                if (cancelled || !Array.isArray(approved)) return;
                const mapped = approved.map((t) => ({
                    id: t.id,
                    quote: t.message,
                    name: t.name,
                    role: null,
                }));
                // Approved first (newest), then static placeholders
                setItems([...mapped, ...STATIC_TESTIMONIALS]);
            } catch (_) {
                /* network errors silently fallback to static */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (name.trim().length < 1) {
            toast.error("Merci d'indiquer votre prénom.");
            return;
        }
        if (message.trim().length < 10) {
            toast.error("Votre message doit faire au moins 10 caractères.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(`${API}/testimonials/submit`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: name.trim(),
                    message: message.trim(),
                }),
            });
            if (!res.ok) throw new Error("submit_failed");
            toast.success("Merci ! Votre avis sera publié après validation.");
            setName("");
            setMessage("");
        } catch (_) {
            toast.error("Une erreur est survenue, merci de réessayer.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="temoignages"
            data-testid="testimonials-section"
            className="py-24 md:py-36 bg-sand/40 relative overflow-hidden"
        >
            <div className="max-w-6xl mx-auto px-6 md:px-12 relative">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8 }}
                    className="max-w-2xl mb-16"
                >
                    <span className="text-xs tracking-[0.25em] uppercase text-rose-700">
                        Témoignages
                    </span>
                    <h2
                        className="font-serif text-4xl md:text-5xl lg:text-6xl text-ink mt-4 leading-tight"
                        data-testid="testimonials-title"
                    >
                        Les mots des
                        <br />
                        <span className="italic text-rose-700">familles</span>.
                    </h2>
                </motion.div>

                <div className="grid md:grid-cols-3 gap-8">
                    {items.slice(0, 6).map((t, i) => (
                        <motion.figure
                            key={t.id}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 0.8, delay: (i % 3) * 0.12 }}
                            data-testid={`testimonial-${t.id}`}
                            className={`relative bg-cream border border-line rounded-3xl p-8 md:p-10 ${
                                i % 3 === 1 ? "md:translate-y-8" : ""
                            }`}
                        >
                            <Quote
                                size={32}
                                strokeWidth={1.5}
                                className="text-rose-300 mb-6"
                            />
                            <blockquote className="font-serif text-xl md:text-2xl text-ink leading-snug italic">
                                « {t.quote} »
                            </blockquote>
                            <figcaption className="mt-8 pt-6 border-t border-line">
                                <p className="font-serif text-lg text-ink">{t.name}</p>
                                {t.role && (
                                    <p className="text-xs tracking-wide text-ink-soft mt-1">
                                        {t.role}
                                    </p>
                                )}
                            </figcaption>
                        </motion.figure>
                    ))}
                </div>

                {/* Submit form */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8 }}
                    className="mt-20 max-w-2xl mx-auto"
                    data-testid="testimonial-form-wrapper"
                >
                    <div className="bg-cream border border-line rounded-3xl p-8 md:p-10">
                        <div className="text-center mb-8">
                            <span className="text-xs tracking-[0.25em] uppercase text-rose-700">
                                Partagez votre expérience
                            </span>
                            <h3 className="font-serif text-3xl md:text-4xl text-ink mt-3 leading-tight">
                                Laissez-moi un{" "}
                                <span className="italic text-rose-700">mot doux</span>.
                            </h3>
                            <p className="mt-4 text-sm text-ink-soft leading-relaxed">
                                Votre avis sera publié après une rapide validation de
                                ma part.
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label
                                    htmlFor="testimonial-name"
                                    className="block text-xs tracking-[0.2em] uppercase text-ink-soft mb-2"
                                >
                                    Votre prénom
                                </label>
                                <input
                                    id="testimonial-name"
                                    data-testid="testimonial-input-name"
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    maxLength={80}
                                    required
                                    placeholder="Léa"
                                    className="w-full bg-sand/50 border border-line rounded-2xl px-5 py-3.5 text-ink placeholder:text-ink-soft/50 focus:outline-none focus:border-rose-300 focus:bg-sand transition-all duration-300"
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="testimonial-message"
                                    className="block text-xs tracking-[0.2em] uppercase text-ink-soft mb-2"
                                >
                                    Votre message
                                </label>
                                <textarea
                                    id="testimonial-message"
                                    data-testid="testimonial-input-message"
                                    rows={5}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    maxLength={1500}
                                    required
                                    placeholder="Partagez votre expérience avec Cindy…"
                                    className="w-full bg-sand/50 border border-line rounded-2xl px-5 py-3.5 text-ink placeholder:text-ink-soft/50 focus:outline-none focus:border-rose-300 focus:bg-sand transition-all duration-300 resize-none"
                                />
                                <p className="text-right text-xs text-ink-soft/70 mt-1">
                                    {message.length}/1500
                                </p>
                            </div>
                            <div className="text-center pt-2">
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="testimonial-submit"
                                    className="inline-flex items-center gap-2 rounded-full bg-rose-700 text-cream px-8 py-3.5 text-sm tracking-wide hover:bg-rose-800 transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2
                                                size={16}
                                                className="animate-spin"
                                            />
                                            Envoi…
                                        </>
                                    ) : (
                                        <>
                                            <Send size={16} />
                                            Envoyer mon avis
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
